import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { isQueryReady } from "src/api/axiosClient/utils";

export const getFieldValuesKey = "getFieldValues" as const;

export type FieldValue = {
    id: number;
    actionFieldId: number;
    value: string;
    position: number;
    dataListOptionCodes?: string[] | null;
};

const FieldValueSchema: z.ZodType<FieldValue> = z.object({
    id: z.number(),
    actionFieldId: z.number(),
    value: z.string(),
    position: z.number(),
    dataListOptionCodes: z.union([z.string().array(), z.null(), z.undefined()]).optional(),
});

const FieldValuesCollectionSchema = z.object({
    "hydra:member": FieldValueSchema.array(),
    "hydra:totalItems": z.number(),
});

type Options = {
    actionPlanActionId: string | undefined;
    enabled?: boolean;
};

export const useGetFieldValues = (options: Options) => {
    const { actionPlanActionId, enabled = true } = options;

    const isEnabled = actionPlanActionId !== undefined && enabled;
    const query = useQuery({
        queryKey: [getFieldValuesKey, actionPlanActionId],
        enabled: isEnabled,
        queryFn: async () => {
            const url = `/api/action_plan_actions/${actionPlanActionId}/field_values`;

            const response = await loggedInApiCall.get(url);
            return response;
        },
        select: (resp) => {
            const data = zodParseWithLog(resp.data, FieldValuesCollectionSchema, getFieldValuesKey);
            const result = data["hydra:member"];
            return result;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        ...query,
        isReady,
    };
};

// Helper function to get a field value by actionFieldId
export const useGetFieldValueByActionFieldId = (
    actionPlanActionId: string | undefined,
    actionFieldId: number | undefined,
    position: number | undefined,
) => {
    const fieldValuesQuery = useGetFieldValues({ actionPlanActionId });

    const fieldValue = fieldValuesQuery.data?.find(
        (fv) => fv.actionFieldId === actionFieldId && (position === undefined || fv.position === position),
    );

    return {
        ...fieldValuesQuery,
        fieldValue,
    };
};
